/**
 * RGM Studio Official Community & A&R Hub — R1 Foundation (Feb 2026)
 * ==================================================================
 *
 * Route: /rgm-studio
 *
 * Read-only surface composed from A-class canonical sources — no
 * artist ownership is transferred, no submission is republished
 * under the official account, and every track / winner / report
 * card links to its canonical target.
 *
 * R1 sections:
 *   1. Header identity (avatar, name, verified/founder badge, follower count)
 *   2. RGM Reports (queries db.reports via existing router)
 *   3. Featured Artists (canonical /api/discover/featured slice)
 *   4. Spotlight Winners (canonical db.spotlight_weeks published)
 *   5. Contest Champions (canonical /api/hall-of-champions)
 *
 * Out of scope for R1: A&R Picks, Official Featured Tracks, Official
 * Playlists, Promotional Reward Coin UI, Community Activity Feed.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import GlobalFooter from "@/components/GlobalFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import BadgeWithModal from "@/components/BadgeWithModal";
import { evolveBadges } from "@/lib/badges";
import { optimizeImage } from "@/lib/mediaUrls";

const FallbackAvatar =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23374151'/%3E%3Ctext x='20' y='26' font-size='18' text-anchor='middle' fill='%239CA3AF' font-family='sans-serif'%3E?%3C/text%3E%3C/svg%3E";

// ── Section wrapper ─────────────────────────────────────────────
export const Section = ({ title, emoji, subtitle, viewAllTo, testId, children }) => (
  <section className="mb-12" data-testid={testId}>
    <div className="flex items-end justify-between mb-5 gap-4">
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          {emoji && <span className="text-2xl">{emoji}</span>} {title}
        </h2>
        {subtitle && (
          <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
      {viewAllTo && (
        <Link
          to={viewAllTo}
          className="text-sm text-purple-300 hover:text-pink-300 transition-colors flex-shrink-0"
          data-testid={`${testId}-view-all`}
        >
          View All →
        </Link>
      )}
    </div>
    {children}
  </section>
);

// ── Report card ─────────────────────────────────────────────────
export const ReportCard = ({ report }) => (
  <Link
    to={`/reports/${report.report_id}`}
    className="group block rounded-2xl overflow-hidden border border-purple-500/20 bg-gradient-to-br from-gray-900/80 to-black hover:border-purple-500/50 transition-all"
    data-testid={`official-report-${report.report_id}`}
  >
    <div className="aspect-[16/10] overflow-hidden bg-gray-900">
      {report.cover_image ? (
        <img
          src={optimizeImage(report.cover_image, 'card')}
          alt={report.title}
          className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-5xl opacity-30">📰</div>
      )}
    </div>
    <div className="p-4">
      {report.category && (
        <span className="inline-block px-2 py-0.5 rounded-full bg-purple-600/20 text-purple-200 text-[10px] font-bold uppercase tracking-widest mb-2">
          {report.category}
        </span>
      )}
      <h3 className="text-white font-bold text-base leading-snug line-clamp-2 group-hover:text-purple-200 transition-colors">
        {report.title}
      </h3>
      {report.source_user_name && (
        <p className="text-xs text-gray-500 mt-2">by {report.source_user_name}</p>
      )}
    </div>
  </Link>
);

// ── Featured artist card ────────────────────────────────────────
export const FeaturedArtistCard = ({ artist, rank }) => (
  <Link
    to={`/artist/${artist.user_id}`}
    className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-purple-900/20 to-transparent border border-purple-500/10 hover:border-purple-500/50 transition-all"
    data-testid={`official-featured-artist-${artist.user_id}`}
  >
    <span className="w-6 text-center text-xs font-black text-purple-300 flex-shrink-0">
      #{rank}
    </span>
    <img
      src={optimizeImage(artist.profile_photo || artist.picture || FallbackAvatar, 'avatar')}
      alt={artist.artist_name || artist.name}
      className="w-12 h-12 rounded-full object-cover border border-purple-500/30 flex-shrink-0"
      onError={(e) => {
        e.target.src = FallbackAvatar;
      }}
    />
    <div className="min-w-0 flex-1">
      <p className="text-white font-semibold truncate">
        {artist.artist_name || artist.name}
      </p>
      <p className="text-xs text-gray-400">
        {(artist.follower_count || 0).toLocaleString()} followers
      </p>
      {artist.badges && artist.badges.length > 0 && (
        <div className="flex gap-1 mt-1">
          {evolveBadges(artist.badges).slice(0, 2).map((b) => (
            <BadgeWithModal key={b} type={b} size="small" showLabel={false} />
          ))}
        </div>
      )}
    </div>
  </Link>
);

// ── Spotlight winner card ───────────────────────────────────────
export const SpotlightCard = ({ row }) => {
  const medal = row.position === 1 ? "🥇" : row.position === 2 ? "🥈" : row.position === 3 ? "🥉" : "";
  return (
    <Link
      to={row.user_id ? `/artist/${row.user_id}` : `/spotlight`}
      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-yellow-900/10 to-transparent border border-yellow-500/20 hover:border-yellow-500/50 transition-all"
      data-testid={`official-spotlight-${row.submission_id}`}
    >
      <span className="text-2xl flex-shrink-0" aria-hidden>{medal || `#${row.position}`}</span>
      {row.cover_url ? (
        <img
          src={optimizeImage(row.cover_url, 'avatar')}
          alt={row.track_title}
          className="w-14 h-14 rounded-lg object-cover border border-yellow-500/30 flex-shrink-0"
        />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-700/40 to-pink-700/40 flex items-center justify-center text-xl flex-shrink-0">
          🎵
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-white font-semibold truncate">
          {row.track_title || "Untitled Track"}
        </p>
        <p className="text-xs text-gray-400 truncate">
          {row.artist_name || "Unknown Artist"}
        </p>
        {row.week_label && (
          <p className="text-[10px] text-yellow-300/70 mt-0.5 uppercase tracking-widest">
            {row.week_label}
          </p>
        )}
      </div>
    </Link>
  );
};

// ── Contest champion card ───────────────────────────────────────
export const ChampionCard = ({ row }) => {
  const medal = row.placement === 1 ? "🥇" : row.placement === 2 ? "🥈" : row.placement === 3 ? "🥉" : "🏆";
  return (
    <Link
      to={row.user_id ? `/artist/${row.user_id}` : "/hall-of-champions"}
      className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-amber-900/10 to-transparent border border-amber-500/20 hover:border-amber-500/50 transition-all"
      data-testid={`official-champion-${row.champion_id || row.entry_ref_id}`}
    >
      <span className="text-2xl flex-shrink-0" aria-hidden>{medal}</span>
      <img
        src={optimizeImage(row.profile_photo || row.entry_cover || FallbackAvatar, 'avatar')}
        alt={row.artist_name}
        className="w-12 h-12 rounded-full object-cover border border-amber-500/30 flex-shrink-0"
        onError={(e) => {
          e.target.src = FallbackAvatar;
        }}
      />
      <div className="min-w-0 flex-1">
        <p className="text-white font-semibold truncate">
          {row.artist_name || "Unknown Artist"}
        </p>
        <p className="text-xs text-gray-400 truncate">
          {row.award_label || row.title}
        </p>
        {row.entry_title && (
          <p className="text-[10px] text-amber-300/70 mt-0.5 truncate">
            {row.entry_title}
          </p>
        )}
      </div>
    </Link>
  );
};

// ── Empty rail ──────────────────────────────────────────────────
export const EmptyRail = ({ label }) => (
  <div className="rounded-xl border border-dashed border-gray-700 bg-gray-900/40 p-6 text-center text-sm text-gray-500">
    {label}
  </div>
);

// ── Header badge pill ───────────────────────────────────────────
// Renders a compact, labeled pill for every badge id the account
// carries. Delegates to the shared BadgeWithModal when the id is
// catalogued there so admins/artists get the standard tooltip modal;
// for header-only badge ids that are not yet in the shared catalog
// (e.g. ``founder_artist``, ``founding_100`` on the RGM Studio
// Official account) it renders a self-contained mini pill so the
// full badge set is visible on mobile without dropping any entry.
const _HEADER_FALLBACK_BADGES = {
  founder_artist: {
    label: "Founder Artist",
    icon: "⭐",
    className: "bg-gradient-to-r from-purple-500 to-pink-600 text-white",
  },
  founding_100: {
    label: "Founding 100",
    icon: "💯",
    className: "bg-gradient-to-r from-amber-400 to-yellow-600 text-black",
  },
};
const HeaderBadge = ({ type }) => {
  if (!type) return null;
  const fallback = _HEADER_FALLBACK_BADGES[type];
  if (fallback) {
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${fallback.className}`}
        title={fallback.label}
        data-testid={`official-header-badge-${type}`}
      >
        <span aria-hidden>{fallback.icon}</span>
        <span>{fallback.label}</span>
      </span>
    );
  }
  return (
    <span data-testid={`official-header-badge-${type}`}>
      <BadgeWithModal type={type} size="small" showLabel={true} />
    </span>
  );
};

// ── Main page ───────────────────────────────────────────────────
const OfficialProfilePage = () => {
  // ``/rgm-studio`` is preserved as a stable alias/entry point.
  // Under the canonical-identity architecture, once the runtime
  // resolver returns an active official account we redirect to that
  // account's public artist profile — so there is a single canonical
  // public RGM presence: ``/artist/{is_official.user_id}``. The
  // Official Hub rails render inline on that profile based solely
  // on ``is_official === true`` (no dedicated tab).
  //
  // The redirect derives the target user_id from the resolver
  // response (never a hardcoded uid), so the runtime identity gate
  // remains ``users.is_official == true`` on the backend.
  const navigate = useNavigate();

  const [state, setState] = useState({
    loading: true,
    active: false,
    account: null,
    reports: [],
    featured_artists: [],
    spotlight_winners: [],
    contest_champions: [],
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/official/profile");
        if (cancelled) return;
        const account = r.data?.account || null;
        const active = !!r.data?.official_active && !!account?.user_id;
        if (active) {
          // Alias → canonical redirect. The Official Hub rails render
          // inline on the resolved artist profile based solely on
          // ``is_official === true`` — there is no internal RGM Studio
          // tab anymore, so no ``?tab=`` query param is needed.
          // ``replace`` keeps browser history clean so Back doesn't
          // loop back to /rgm-studio.
          navigate(
            `/artist/${account.user_id}`,
            { replace: true },
          );
          return;
        }
        setState({
          loading: false,
          active: false,
          account,
          reports: r.data?.reports || [],
          featured_artists: r.data?.featured_artists || [],
          spotlight_winners: r.data?.spotlight_winners || [],
          contest_champions: r.data?.contest_champions || [],
        });
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false }));
        toast.error("Could not load the official RGM Studio profile.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const a = state.account;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="official-profile-page">
      <SharedNavigation />

      {/* Header identity */}
      <div className="relative overflow-hidden border-b border-purple-500/20">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-black to-pink-900/20 pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 py-8 md:py-14">
          <div
            className="rounded-2xl bg-white/[0.04] border border-purple-500/30 p-5 md:p-8 shadow-xl backdrop-blur-sm"
            data-testid="official-profile-identity-card"
          >
            <div className="flex items-center gap-4 md:gap-8 flex-col md:flex-row md:items-start">
              <img
                src={optimizeImage(a?.profile_photo || a?.picture || FallbackAvatar, 'avatar')}
                alt={a?.artist_name || "RGM Studio"}
                className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover border-2 border-purple-500/40 shadow-xl flex-shrink-0"
                data-testid="official-profile-avatar"
                onError={(e) => {
                  const pic = a?.picture;
                  if (pic && !e.target.dataset.pictureTried) {
                    e.target.dataset.pictureTried = "1";
                    e.target.src = pic;
                  } else {
                    e.target.src = FallbackAvatar;
                  }
                }}
              />
              <div className="min-w-0 flex-1 w-full text-center md:text-left">
                <p className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-bold mb-2">
                  Official Community & A&R Hub
                </p>
                <h1
                  className="text-2xl md:text-5xl font-black text-white leading-tight flex flex-wrap items-center justify-center md:justify-start gap-x-3 gap-y-2"
                  data-testid="official-profile-title"
                >
                  <span className="break-words">
                    {a?.artist_name || "RGM Studio"}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 border border-sky-400/40 px-2.5 py-0.5 text-[11px] md:text-xs font-bold text-sky-200 whitespace-nowrap align-middle"
                    aria-label="Verified official"
                    data-testid="official-profile-verified-pill"
                  >
                    <span aria-hidden>✓</span>
                    <span>Verified official</span>
                  </span>
                </h1>
                {a?.bio && (
                  <p className="text-gray-300 mt-3 max-w-2xl leading-relaxed text-sm md:text-base whitespace-pre-line mx-auto md:mx-0">
                    {a.bio}
                  </p>
                )}
                <div
                  className="mt-4 flex items-center gap-2 text-sm text-gray-300 justify-center md:justify-start"
                  data-testid="official-profile-stats"
                >
                  {a?.follower_count !== undefined && (
                    <span data-testid="official-profile-followers">
                      <strong className="text-white">{a.follower_count.toLocaleString()}</strong>
                      <span className="text-gray-400"> followers</span>
                    </span>
                  )}
                </div>
                {a?.badges && a.badges.length > 0 && (
                  <div
                    className="mt-3 flex flex-wrap items-center gap-2 justify-center md:justify-start"
                    data-testid="official-profile-badges"
                  >
                    {evolveBadges(a.badges).map((b) => (
                      <HeaderBadge key={b} type={b} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {state.loading ? (
          <p className="text-gray-500 text-center py-20" data-testid="official-profile-loading">
            Loading…
          </p>
        ) : !state.active ? (
          <Card className="p-8 text-center bg-gray-900/60 border-purple-500/20" data-testid="official-profile-inactive">
            <p className="text-2xl font-bold text-white mb-2">Official profile not yet configured</p>
            <p className="text-gray-400 text-sm max-w-xl mx-auto">
              An administrator has not marked an RGM Studio account as official on this environment.
              Set <code className="px-2 py-0.5 rounded bg-black/60 text-purple-200">users.is_official = true</code>
              &nbsp;on the RGM Studio Official account to activate this page.
            </p>
          </Card>
        ) : (
          <>
            {/* 1. RGM Reports */}
            <Section
              title="RGM Reports"
              emoji="📰"
              subtitle="News, winners, and platform announcements — always live."
              viewAllTo="/reports"
              testId="official-section-reports"
            >
              {state.reports.length === 0 ? (
                <EmptyRail label="No published reports yet." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {state.reports.map((r) => (
                    <ReportCard key={r.report_id} report={r} />
                  ))}
                </div>
              )}
            </Section>

            {/* 2. Featured Artists */}
            <Section
              title="Featured Artists"
              emoji="⭐"
              subtitle="Highlighted artists across RGM Studio right now."
              viewAllTo="/explore"
              testId="official-section-featured"
            >
              {state.featured_artists.length === 0 ? (
                <EmptyRail label="No featured artists to show yet." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {state.featured_artists.map((artist, i) => (
                    <FeaturedArtistCard
                      key={artist.user_id}
                      artist={artist}
                      rank={i + 1}
                    />
                  ))}
                </div>
              )}
            </Section>

            {/* 3. Spotlight Winners */}
            <Section
              title="Spotlight Winners"
              emoji="🌟"
              subtitle="Recent published Spotlight weeks — canonical winners."
              viewAllTo="/spotlight"
              testId="official-section-spotlight"
            >
              {state.spotlight_winners.length === 0 ? (
                <EmptyRail label="No Spotlight winners published yet." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {state.spotlight_winners.map((row) => (
                    <SpotlightCard key={`${row.spotlight_id}-${row.submission_id}`} row={row} />
                  ))}
                </div>
              )}
            </Section>

            {/* 4. Contest Champions */}
            <Section
              title="Contest Champions"
              emoji="🏆"
              subtitle="Recent Hall of Champions honors."
              viewAllTo="/winners"
              testId="official-section-champions"
            >
              {state.contest_champions.length === 0 ? (
                <EmptyRail label="No contest champions to show yet." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {state.contest_champions.map((row) => (
                    <ChampionCard key={row.champion_id || row.entry_ref_id} row={row} />
                  ))}
                </div>
              )}
            </Section>
          </>
        )}
      </div>

      <GlobalFooter />
    </div>
  );
};

export default OfficialProfilePage;
